/** Cerebro: búsqueda y orden de notas y listas (puro). */
import type { Note } from '@habitos/protocol';
import type { ListItemRec, NoteRec } from './model';

export type NoteKind = Note['kind'];
export const NOTE_KINDS: NoteKind[] = ['note', 'idea', 'fact', 'quote', 'contact', 'link', 'date', 'list'];

/** Minúsculas y sin acentos, para buscar "cafe" y encontrar "Café". */
export function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export interface NoteFilter {
  query?: string;
  kind?: NoteKind | 'all' | 'notes';
  tag?: string | null;
}

/** Fijadas primero; después, las más recientes. */
export function sortNotes(notes: NoteRec[]): NoteRec[] {
  return [...notes].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? 1 : -1;
    // Desempate estable: un comparador incoherente con fechas iguales ordenaría al azar.
    return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
  });
}

/** Elementos vivos de una lista, en su orden. */
export function itemsOf(items: ListItemRec[], listId: string): ListItemRec[] {
  return items.filter((i) => i.listId === listId && !i.deletedAt).sort((a, b) => (a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0));
}

export function searchNotes(notes: NoteRec[], f: NoteFilter = {}, items: ListItemRec[] = []): NoteRec[] {
  const q = normalize(f.query ?? '');
  const words = q ? q.split(/\s+/) : [];
  return sortNotes(
    notes.filter((n) => {
      if (n.deletedAt) return false;
      if (f.kind === 'notes' && n.kind === 'list') return false;
      if (f.kind && f.kind !== 'all' && f.kind !== 'notes' && n.kind !== f.kind) return false;
      if (f.tag && !n.tags.includes(f.tag)) return false;
      if (!words.length) return true;
      const listText = n.kind === 'list' ? itemsOf(items, n.id).map((i) => i.text).join(' ') : '';
      const haystack = normalize([n.title, n.body ?? '', n.url ?? '', n.tags.join(' '), listText].join(' '));
      return words.every((w) => haystack.includes(w));
    }),
  );
}

export function allTags(notes: NoteRec[]): string[] {
  return [...new Set(notes.filter((n) => !n.deletedAt).flatMap((n) => n.tags))].sort((a, b) => a.localeCompare(b));
}

/** "salud, Lectura ,salud" -> ["salud", "lectura"] */
export function parseTags(input: string): string[] {
  return [...new Set(input.split(',').map((t) => normalize(t).slice(0, 30)).filter(Boolean))].slice(0, 20);
}

export function notesLinkedTo(notes: NoteRec[], habitId: string): NoteRec[] {
  return sortNotes(notes.filter((n) => !n.deletedAt && n.habitIds.includes(habitId)));
}

/** Lista existente cuyo nombre coincide (sin acentos ni mayúsculas). */
export function findList(notes: NoteRec[], name: string): NoteRec | undefined {
  const target = normalize(name);
  return notes.find((n) => !n.deletedAt && n.kind === 'list' && normalize(n.title) === target);
}

